/**
 * useBoardSpacePan - Figma/Miro-style space-bar pan with momentum
 *
 * Hold Space → cursor becomes grab (open hand)
 * Space + Click + Drag → board scrolls with mouse, cursor grabbing (closed fist)
 * Release → momentum/inertia continues scrolling
 */

import { useRef, useEffect } from 'react';
import { setDragBodyStyles, stopDragBodyStyles } from '../../lib/dragResizeUtils';

// ============================================
// Constants
// ============================================

const VELOCITY_WINDOW_MS = 100;
const MAX_VELOCITY_SAMPLES = 8;
const MOMENTUM_DECAY = 0.95;
const MOMENTUM_MIN_VELOCITY = 0.5;

// ============================================
// Types
// ============================================

interface VelocitySample {
  x: number;
  y: number;
  timestamp: number;
}

export interface UseSpacePanOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  isDndDragging: boolean;
  isZoomDragging: boolean;
  isSidebarResizing: boolean;
  isHeaderResizing: boolean;
  isDetailResizing?: boolean;
}

// ============================================
// Hook
// ============================================

export function useBoardSpacePan({
  containerRef,
  isDndDragging,
  isZoomDragging,
  isSidebarResizing,
  isHeaderResizing,
  isDetailResizing = false,
}: UseSpacePanOptions): void {
  // Refs for guard values (avoid stale closures in event listeners)
  const isDndDraggingRef = useRef(isDndDragging);
  const isZoomDraggingRef = useRef(isZoomDragging);
  const isSidebarResizingRef = useRef(isSidebarResizing);
  const isHeaderResizingRef = useRef(isHeaderResizing);
  const isDetailResizingRef = useRef(isDetailResizing);

  isDndDraggingRef.current = isDndDragging;
  isZoomDraggingRef.current = isZoomDragging;
  isSidebarResizingRef.current = isSidebarResizing;
  isHeaderResizingRef.current = isHeaderResizing;
  isDetailResizingRef.current = isDetailResizing;

  // Stop momentum when dnd drag starts
  const momentumFrameRef = useRef<number | null>(null);
  useEffect(() => {
    if (isDndDragging && momentumFrameRef.current !== null) {
      cancelAnimationFrame(momentumFrameRef.current);
      momentumFrameRef.current = null;
    }
  }, [isDndDragging]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // ---- State (mutable, not React state for perf) ----
    type Phase = 'idle' | 'ready' | 'panning';
    let phase: Phase = 'idle';
    let lastMouseX = 0;
    let lastMouseY = 0;
    const velocitySamples: VelocitySample[] = [];

    function isAnyGuardActive(): boolean {
      return (
        isDndDraggingRef.current ||
        isZoomDraggingRef.current ||
        isSidebarResizingRef.current ||
        isHeaderResizingRef.current ||
        isDetailResizingRef.current
      );
    }

    function isFocusInEditable(): boolean {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return true;
      if ((el as HTMLElement).isContentEditable) return true;
      return false;
    }

    function stopMomentum(): void {
      if (momentumFrameRef.current !== null) {
        cancelAnimationFrame(momentumFrameRef.current);
        momentumFrameRef.current = null;
      }
    }

    function setPhase(next: Phase): void {
      phase = next;
      if (next === 'idle') {
        stopDragBodyStyles();
      } else if (next === 'ready') {
        setDragBodyStyles('grab');
      } else if (next === 'panning') {
        setDragBodyStyles('grabbing');
      }
    }

    function startMomentum(): void {
      const now = performance.now();
      const cutoff = now - VELOCITY_WINDOW_MS;
      const recent = velocitySamples.filter(s => s.timestamp >= cutoff);
      if (recent.length < 2) return;

      const first = recent[0];
      const last = recent[recent.length - 1];
      const dt = (last.timestamp - first.timestamp) / 1000; // seconds
      if (dt < 0.001) return;

      let vx = (last.x - first.x) / dt; // px/sec
      let vy = (last.y - first.y) / dt;

      let lastTime = performance.now();

      function tick(): void {
        const now = performance.now();
        const dtFrame = (now - lastTime) / 1000;
        lastTime = now;

        const decayFactor = Math.pow(MOMENTUM_DECAY, dtFrame * 60);
        vx *= decayFactor;
        vy *= decayFactor;

        const dx = vx * dtFrame;
        const dy = vy * dtFrame;

        if (Math.abs(vx) < MOMENTUM_MIN_VELOCITY && Math.abs(vy) < MOMENTUM_MIN_VELOCITY) {
          momentumFrameRef.current = null;
          return;
        }

        const c = containerRef.current;
        if (c) {
          c.scrollLeft -= dx;
          c.scrollTop -= dy;
        }

        momentumFrameRef.current = requestAnimationFrame(tick);
      }

      momentumFrameRef.current = requestAnimationFrame(tick);
    }

    // ---- Event handlers ----

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.code !== 'Space' || e.repeat) return;
      if (isFocusInEditable()) return;
      if (isAnyGuardActive()) return;

      e.preventDefault();
      stopMomentum();

      if (phase === 'idle') {
        setPhase('ready');
      }
    }

    function handleKeyUp(e: KeyboardEvent): void {
      if (e.code !== 'Space') return;

      if (phase === 'panning') {
        startMomentum();
        setPhase('idle');
      } else if (phase === 'ready') {
        setPhase('idle');
      }
    }

    // Some browsers trigger the default Space scroll on keypress, not keydown.
    // Block it whenever we're in ready or panning phase.
    function handleKeyPress(e: KeyboardEvent): void {
      if (e.code === 'Space' && phase !== 'idle') {
        e.preventDefault();
      }
    }

    function handlePointerDown(e: PointerEvent): void {
      if (phase !== 'ready') return;
      // Only primary button
      if (e.button !== 0) return;

      e.preventDefault(); // blocks dnd-kit (checks nativeEvent.defaultPrevented)
      e.stopPropagation();

      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
      velocitySamples.length = 0;
      velocitySamples.push({ x: e.clientX, y: e.clientY, timestamp: performance.now() });

      setPhase('panning');
    }

    function handlePointerMove(e: PointerEvent): void {
      if (phase !== 'panning') return;

      const deltaX = e.clientX - lastMouseX;
      const deltaY = e.clientY - lastMouseY;
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;

      const c = containerRef.current;
      if (c) {
        c.scrollLeft -= deltaX;
        c.scrollTop -= deltaY;
      }

      // Record velocity sample
      const now = performance.now();
      velocitySamples.push({ x: e.clientX, y: e.clientY, timestamp: now });
      // Trim old samples
      while (velocitySamples.length > MAX_VELOCITY_SAMPLES) {
        velocitySamples.shift();
      }
    }

    function handlePointerUp(e: PointerEvent): void {
      if (phase !== 'panning') return;
      if (e.button !== 0) return;

      startMomentum();
      setPhase('ready');
    }

    function handleClick(e: MouseEvent): void {
      if (phase !== 'idle') {
        e.stopPropagation();
        e.preventDefault();
      }
    }

    function handleReset(): void {
      if (phase !== 'idle') {
        stopMomentum();
        setPhase('idle');
      }
    }

    // ---- Register listeners ----
    // Capture phase for key events ensures we fire before any other handler
    // and can preventDefault before the browser's default scroll-on-Space.
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener('keyup', handleKeyUp, { capture: true });
    window.addEventListener('keypress', handleKeyPress, { capture: true });
    container.addEventListener('pointerdown', handlePointerDown, { capture: true });
    container.addEventListener('click', handleClick, { capture: true });
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('blur', handleReset);
    document.addEventListener('visibilitychange', handleReset);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      window.removeEventListener('keyup', handleKeyUp, { capture: true });
      window.removeEventListener('keypress', handleKeyPress, { capture: true });
      container.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      container.removeEventListener('click', handleClick, { capture: true });
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('blur', handleReset);
      document.removeEventListener('visibilitychange', handleReset);
      stopMomentum();
      if (phase !== 'idle') {
        stopDragBodyStyles();
      }
    };
  }, []); // stable - all values read from refs
}
